/**
 * Past Projects Portfolio Manager
 * Showcase past projects for RFP responses with similarity matching and past performance library
 * Entity Type: past_project
 */

import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Button, Input, Badge, EmptyState, Select } from '@tracekit/core';
import { useWorkspace, queryEntities } from '@tracekit/core';
import { PageSkeleton } from '../components/LoadingSkeleton';
import {
  Search,
  Filter,
  Download,
  Plus,
  Edit2,
  ChevronDown,
  ChevronRight,
  Calendar,
  Users,
  DollarSign,
  Building2,
  MapPin,
  Award,
  Star,
  Image as ImageIcon,
  FileText,
  TrendingUp,
  CheckCircle,
  Target,
} from 'lucide-react';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { useToastStore } from '@tracekit/core';
import { useWorkplanGeneration } from '../contexts/WorkplanGenerationContext'; // NEW: Phase 5
import { WorkplanGenerationModal } from '../components/WorkplanGenerationModal'; // NEW: Phase 5

// Entity type from Policy Pack v2.0
interface PastProjectEntity {
  id: string;
  entity_type: string;
  data: {
    project_id?: string;
    project_number?: string; // NEW: Excel project number
    artifact_id?: string;
    project_name: string;
    client_name: string;
    project_type?: string;
    service_line?: string;
    project_status?: 'completed' | 'ongoing' | 'planned' | 'on_hold';
    project_phase?: string; // NEW: Primary phase name (e.g., "Phase 1 ESA")
    phase_type?: string; // NEW: Phase classification (e.g., "ESA", "DSI")
    related_phases?: string[]; // NEW: Array of all phase names for this project
    phase_project_metadata?: { // NEW: Detailed phase metadata
      phases?: Array<{
        phase_id: string;
        phase_number: string;
        phase_name: string;
        phase_type?: string;
        row_index?: number;
      }>;
      total_phases?: number;
      primary_phase?: {
        phase_id: string;
        phase_name: string;
        phase_type?: string;
      };
      excel_source?: string;
    };
    contract_value?: number;
    start_date?: string;
    completion_date?: string;
    duration_months?: number;
    geographic_location?: string;
    project_location?: string; // Also used for location
    scope_description?: string;
    scope_summary?: string; // Also used for scope
    technical_approach?: string;
    key_deliverables?: string[];
    outcomes_achieved?: string[];
    challenges_overcome?: string[];
    client_satisfaction_score?: number;
    team_size?: number;
    key_personnel?: string[];
    awards_recognition?: string[];
    photos_available?: boolean;
    references_available?: boolean;
    similar_to_rfp_types?: string[];
  };
  created_at?: string;
  updated_at?: string;
}

interface FilterState {
  client: string;
  projectType: string;
  serviceLine: string;
  projectStatus: string; // NEW: Phase 1
  dateFrom: string;
  dateTo: string;
  minValue: string;
  maxValue: string;
  minSatisfaction: string;
}

export function ProjectPortfolioPage() {
  const { projectId } = useWorkspace();
  const { addToast } = useToastStore();
  const { selectedApproaches, selectedCompliance, setSelectedProject } = useWorkplanGeneration(); // NEW: Phase 5
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [showGenerationModal, setShowGenerationModal] = useState(false); // NEW: Phase 5
  const [projectForGeneration, setProjectForGeneration] = useState<PastProjectEntity | null>(null); // NEW: Phase 5
  const [sortBy, setSortBy] = useState<'date' | 'start_date' | 'value' | 'satisfaction' | 'name'>('date'); // NEW: Phase 1 - added start_date, name
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc'); // NEW: Phase 1 - bidirectional sorting
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20); // Fixed page size
  const [filters, setFilters] = useState<FilterState>({
    client: '',
    projectType: '',
    serviceLine: 'all',
    projectStatus: 'all', // NEW: Phase 1
    dateFrom: '',
    dateTo: '',
    minValue: '',
    maxValue: '',
    minSatisfaction: '',
  });

  // Fetch past project entities with pagination
  const { data: projectsData, isLoading, error } = useQuery({
    queryKey: ['past-project-entities', projectId, page, pageSize],
    queryFn: async () => {
      if (!projectId) throw new Error('Project ID required');
      const result = await queryEntities({
        project_id: projectId,
        entity_type: 'past_project',
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      
      return {
        entities: result.entities.map(e => ({
          id: e.id,
          entity_type: e.entity_type || 'past_project',
          data: (e.data || {}) as PastProjectEntity['data'],
          created_at: e.created_at || new Date().toISOString(),
          updated_at: e.updated_at,
        })) as PastProjectEntity[],
        total: result.total_count,
        hasMore: result.pagination.has_more,
      };
    },
    enabled: !!projectId,
  });

  const projects = projectsData?.entities || [];
  const totalCount = projectsData?.total || 0;
  const hasMore = projectsData?.hasMore || false;

  // Filtered and sorted projects
  const filteredProjects = useMemo(() => {
    if (!projects) return [];
    
    let filtered = projects.filter((project) => {
      // Search query
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch =
          project.data.project_name?.toLowerCase().includes(searchLower) ||
          project.data.client_name?.toLowerCase().includes(searchLower) ||
          project.data.project_type?.toLowerCase().includes(searchLower) ||
          project.data.scope_description?.toLowerCase().includes(searchLower) ||
          project.data.geographic_location?.toLowerCase().includes(searchLower);
        
        if (!matchesSearch) return false;
      }
      
      // Client filter
      if (filters.client && !project.data.client_name?.toLowerCase().includes(filters.client.toLowerCase())) {
        return false;
      }
      
      // Project type filter
      if (filters.projectType && !project.data.project_type?.toLowerCase().includes(filters.projectType.toLowerCase())) {
        return false;
      }
      
      // Service line filter
      if (filters.serviceLine !== 'all' && project.data.service_line !== filters.serviceLine) {
        return false;
      }
      
      // Project status filter (NEW: Phase 1)
      if (filters.projectStatus !== 'all') {
        // Infer status from completion_date if not explicitly set
        const status = project.data.project_status || 
          (project.data.completion_date ? 'completed' : 'ongoing');
        if (status !== filters.projectStatus) {
          return false;
        }
      }
      
      // Date filters
      if (filters.dateFrom && project.data.completion_date) {
        if (new Date(project.data.completion_date) < new Date(filters.dateFrom)) {
          return false;
        }
      }
      if (filters.dateTo && project.data.completion_date) {
        if (new Date(project.data.completion_date) > new Date(filters.dateTo)) {
          return false;
        }
      }
      
      // Contract value filters
      if (filters.minValue && project.data.contract_value) {
        if (project.data.contract_value < parseFloat(filters.minValue)) {
          return false;
        }
      }
      if (filters.maxValue && project.data.contract_value) {
        if (project.data.contract_value > parseFloat(filters.maxValue)) {
          return false;
        }
      }
      
      // Satisfaction filter
      if (filters.minSatisfaction && project.data.client_satisfaction_score) {
        if (project.data.client_satisfaction_score < parseFloat(filters.minSatisfaction)) {
          return false;
        }
      }
      
      return true;
    });
    
    // Sort (NEW: Phase 1 - bidirectional sorting for all fields)
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          const dateA = a.data.completion_date ? new Date(a.data.completion_date).getTime() : 0;
          const dateB = b.data.completion_date ? new Date(b.data.completion_date).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'start_date':
          const startA = a.data.start_date ? new Date(a.data.start_date).getTime() : 0;
          const startB = b.data.start_date ? new Date(b.data.start_date).getTime() : 0;
          comparison = startA - startB;
          break;
        case 'value':
          comparison = (a.data.contract_value || 0) - (b.data.contract_value || 0);
          break;
        case 'satisfaction':
          comparison = (a.data.client_satisfaction_score || 0) - (b.data.client_satisfaction_score || 0);
          break;
        case 'name':
          comparison = (a.data.project_name || '').localeCompare(b.data.project_name || '');
          break;
        default:
          comparison = 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [projects, searchQuery, filters, sortBy, sortDirection]); // NEW: Phase 1 - added sortDirection

  // Calculate statistics from ALL data (not just current page)
  // NOTE: With pagination, we only have the current page's data, so stats will be limited
  // For accurate totals, we'd need a separate API call to get aggregate stats
  const stats = useMemo(() => {
    if (!filteredProjects) return {
      total: totalCount, // Use total from API
      totalValue: 0,
      avgSatisfaction: 0,
      withAwards: 0,
      pageTotal: 0,
    };
    
    const pageTotal = filteredProjects.length;
    // Calculate from current page only (with disclaimer)
    const totalValue = filteredProjects.reduce((sum, p) => {
      const value = typeof p.data.contract_value === 'string' 
        ? parseFloat(p.data.contract_value) 
        : (p.data.contract_value || 0);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
    
    const satisfactionScores = filteredProjects.filter(p => p.data.client_satisfaction_score).map(p => p.data.client_satisfaction_score!);
    const avgSatisfaction = satisfactionScores.length > 0 ? satisfactionScores.reduce((a, b) => a + b, 0) / satisfactionScores.length : 0;
    
    const withAwards = filteredProjects.filter(p => p.data.awards_recognition && p.data.awards_recognition.length > 0).length;
    
    return { 
      total: totalCount, // Total from API
      pageTotal, 
      totalValue, 
      avgSatisfaction, 
      withAwards 
    };
  }, [filteredProjects, totalCount]);

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const handleClearFilters = () => {
    setFilters({
      client: '',
      projectType: '',
      serviceLine: 'all',
      projectStatus: 'all', // NEW: Phase 1
      dateFrom: '',
      dateTo: '',
      minValue: '',
      maxValue: '',
      minSatisfaction: '',
    });
  };

  const handleExport = (format: 'csv' | 'json') => {
    if (format === 'csv') {
      const headers = [
        'Project Name', 'Client', 'Project Type', 'Service Line', 'Contract Value', 
        'Start Date', 'Completion Date', 'Duration (Months)', 'Location', 'Satisfaction Score'
      ];
      const rows = filteredProjects.map((project) => [
        project.data.project_name || 'N/A',
        project.data.client_name || 'N/A',
        project.data.project_type || 'N/A',
        project.data.service_line || 'N/A',
        project.data.contract_value?.toString() || '0',
        project.data.start_date || 'N/A',
        project.data.completion_date || 'N/A',
        project.data.duration_months?.toString() || '0',
        project.data.geographic_location || 'N/A',
        project.data.client_satisfaction_score?.toString() || 'N/A',
      ]);
      const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `past-projects-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const blob = new Blob([JSON.stringify(filteredProjects, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `past-projects-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleAddToProposal = (project: PastProjectEntity) => {
    addToast({
      type: 'success',
      title: 'Added to proposal',
      message: `${project.data.project_name} added as past performance example`,
    });
  };

  // NEW: Phase 5 - Workplan generation handlers
  const handleGenerateWorkplan = (project: PastProjectEntity) => {
    setProjectForGeneration(project);
    setSelectedProject(project);
    setShowGenerationModal(true);
  };

  const handleWorkplanGenerated = (reportId: string) => {
    addToast({
      type: 'success',
      title: 'Workplan Generated',
      message: `Workplan for ${projectForGeneration?.data.project_name} has been generated successfully`,
    });
    setShowGenerationModal(false);
    setProjectForGeneration(null);
  };

  if (!projectId) {
    return (
      <div className="p-6">
        <Card>
          <div className="text-center py-8">
            <p className="text-gray-600">Please select a project to view past projects portfolio.</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Breadcrumbs items={[
        { label: 'RFP Proposals', path: '/rfp/proposals' },
        { label: 'Past Projects Portfolio' },
      ]} />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <Building2 className="w-8 h-8" />
          Past Projects Portfolio
        </h1>
        <p className="text-gray-600 mt-1">
          Showcase past projects for RFP responses with similarity matching and performance tracking
        </p>
      </div>

      {/* Stats Cards */}
      {projects && projects.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="p-6 hover:shadow-lg transition-shadow duration-200">
            <div className="text-sm text-gray-600 font-medium">Total Projects</div>
            <div className="text-3xl font-bold mt-2">{totalCount.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-2">Across all pages</div>
          </Card>
          <Card className="p-6 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 hover:shadow-lg transition-shadow duration-200 text-gray-900 dark:text-gray-100">
            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Current Page Value</div>
            <div className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
              {stats.totalValue > 0 
                ? `$${(stats.totalValue / 1000000).toFixed(2)}M` 
                : '$0'}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">Page {page} of {Math.ceil(stats.total / pageSize)}</div>
          </Card>
          <Card className="p-6 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 hover:shadow-lg transition-shadow duration-200 text-gray-900 dark:text-gray-100">
            <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Page Avg Satisfaction</div>
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 flex items-center gap-2 mt-2">
              {stats.avgSatisfaction > 0 ? (
                <>
                  {stats.avgSatisfaction.toFixed(1)}/5.0
                  <Star className="w-6 h-6 fill-current" />
                </>
              ) : (
                <span className="text-xl">0.0/5.0</span>
              )}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">Current page only</div>
          </Card>
          <Card className="p-6 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 hover:shadow-lg transition-shadow duration-200 text-gray-900 dark:text-gray-100">
            <div className="text-sm text-gray-600 font-medium">Page With Awards</div>
            <div className="text-3xl font-bold text-purple-600 mt-2">
              {stats.withAwards}
            </div>
            <div className="text-xs text-gray-500 mt-2">Current page only</div>
          </Card>
        </div>
      )}

      {/* Search & Filters */}
      <Card className="mb-6">
        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search projects by name, client, type, or location..."
                className="pl-10"
              />
            </div>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'start_date' | 'value' | 'satisfaction' | 'name')}
              className="w-36"
            >
              <option value="date">Sort by Completion Date</option>
              <option value="start_date">Sort by Start Date</option>
              <option value="value">Sort by Value</option>
              <option value="satisfaction">Sort by Satisfaction</option>
              <option value="name">Sort by Name</option>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setSortDirection(d => d === 'asc' ? 'desc' : 'asc')}
              className="w-24"
              title={sortDirection === 'asc' ? 'Currently ascending - click to sort descending' : 'Currently descending - click to sort ascending'}
            >
              {sortDirection === 'asc' ? '↑ Asc' : '↓ Desc'}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              title="Show/hide advanced filters for past projects"
            >
              <Filter className="h-4 w-4 mr-2" />
              Filters
              {Object.values(filters).filter(v => v && v !== 'all').length > 0 && (
                <Badge variant="default" className="ml-2">
                  {Object.values(filters).filter(v => v && v !== 'all').length}
                </Badge>
              )}
            </Button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="border-t pt-4 space-y-4">
              <div className="text-sm text-gray-600 mb-2">
                <p>Filter past projects by client, project type, service line, status, date range, contract value, and satisfaction score. Filters are applied in real-time.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Client</label>
                  <Input
                    value={filters.client}
                    onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                    placeholder="Filter by client name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Project Type</label>
                  <Input
                    value={filters.projectType}
                    onChange={(e) => setFilters({ ...filters, projectType: e.target.value })}
                    placeholder="Filter by project type"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Service Line</label>
                  <Select
                    value={filters.serviceLine}
                    onChange={(e) => setFilters({ ...filters, serviceLine: e.target.value })}
                  >
                    <option value="all">All</option>
                    <option value="Contaminated Sites Assessment & Remediation">Contaminated Sites</option>
                    <option value="Environmental Engineering">Environmental Engineering</option>
                    <option value="Biology & Environmental Impact Assessment">Biology/EIA</option>
                    <option value="Indoor Air Quality & Hazardous Materials">IAQ/HazMat</option>
                    <option value="EHS Management & Compliance">EHS Management</option>
                    <option value="Environmental Spill Response">Spill Response</option>
                    <option value="Landfill Management">Landfill Management</option>
                    <option value="Forestry">Forestry</option>
                    <option value="Indigenous Engagement">Indigenous Engagement</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Project Status</label>
                  <Select
                    value={filters.projectStatus}
                    onChange={(e) => setFilters({ ...filters, projectStatus: e.target.value })}
                  >
                    <option value="all">All</option>
                    <option value="completed">Completed</option>
                    <option value="ongoing">Ongoing</option>
                    <option value="planned">Planned</option>
                    <option value="on_hold">On Hold</option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Completion From</label>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Completion To</label>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Satisfaction Score</label>
                  <Input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={filters.minSatisfaction}
                    onChange={(e) => setFilters({ ...filters, minSatisfaction: e.target.value })}
                    placeholder="0-5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Min Contract Value ($)</label>
                  <Input
                    type="number"
                    value={filters.minValue}
                    onChange={(e) => setFilters({ ...filters, minValue: e.target.value })}
                    placeholder="Minimum value"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Max Contract Value ($)</label>
                  <Input
                    type="number"
                    value={filters.maxValue}
                    onChange={(e) => setFilters({ ...filters, maxValue: e.target.value })}
                    placeholder="Maximum value"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClearFilters}>
                  Clear All
                </Button>
                <Button onClick={() => setShowFilters(false)}>Apply Filters</Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Action Bar */}
      {projects && (
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600">
            Showing {filteredProjects.length} of {projects.length} projects
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => handleExport('csv')}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => handleExport('json')}>
              <Download className="h-4 w-4 mr-2" />
              Export JSON
            </Button>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Add Project
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && <PageSkeleton />}

      {/* Error State */}
      {error && (
        <Card>
          <div className="text-center py-8">
            <p className="text-red-600 mb-4">Error loading projects: {error instanceof Error ? error.message : 'Unknown error'}</p>
            <Button onClick={() => window.location.reload()}>Retry</Button>
          </div>
        </Card>
      )}

      {/* Empty State */}
      {!isLoading && (!projects || projects.length === 0) && (
        <Card>
          <EmptyState
            icon={<Building2 className="w-12 h-12" />}
            title="No Past Projects Found"
            description="No past project records found in the database. Upload project documentation or run entity extraction to populate this portfolio."
          />
        </Card>
      )}

      {/* Projects Grid */}
      {!isLoading && projects && projects.length > 0 && (
        <>
          {filteredProjects.length === 0 ? (
            <Card>
              <div className="text-center py-12 text-gray-500">
                <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No projects found matching your search</p>
                <p className="text-sm mt-2">Try adjusting your filters or search query</p>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredProjects.map((project) => {
                const isExpanded = expandedRows.has(project.id);
                const isSelected = selectedProjects.has(project.id);

                return (
                  <Card key={project.id} className={`${isSelected ? 'ring-2 ring-blue-500' : ''} hover:shadow-lg transition-shadow`}>
                    <div className="space-y-3">
                      {/* Header */}
                      <div
                        className="cursor-pointer"
                        onClick={() => toggleRow(project.id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {isExpanded ? <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold truncate">{project.data.project_name}</h3>
                              <p className="text-sm text-gray-600 truncate">{project.data.client_name}</p>
                            </div>
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              e.stopPropagation();
                              if (e.target.checked) {
                                setSelectedProjects(new Set([...selectedProjects, project.id]));
                              } else {
                                const newSet = new Set(selectedProjects);
                                newSet.delete(project.id);
                                setSelectedProjects(newSet);
                              }
                            }}
                            className="flex-shrink-0"
                          />
                        </div>

                        {/* Badges */}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {project.data.service_line && (
                            <Badge variant="outline">{project.data.service_line}</Badge>
                          )}
                          {project.data.contract_value && (
                            <Badge variant="secondary">
                              <DollarSign className="w-3 h-3 mr-1" />
                              ${project.data.contract_value.toLocaleString()}
                            </Badge>
                          )}
                          {project.data.client_satisfaction_score && (
                            <Badge variant="default" className="flex items-center gap-1">
                              <Star className="w-3 h-3 fill-current" />
                              {project.data.client_satisfaction_score}/5
                            </Badge>
                          )}
                          {project.data.awards_recognition && project.data.awards_recognition.length > 0 && (
                            <Badge variant="default" className="bg-purple-600">
                              <Award className="w-3 h-3 mr-1" />
                              Award
                            </Badge>
                          )}
                        </div>

                        {/* Quick Info */}
                        <div className="text-sm text-gray-600 space-y-1 mt-2">
                          {project.data.geographic_location && (
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              <span>{project.data.geographic_location}</span>
                            </div>
                          )}
                          {project.data.completion_date && (
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4" />
                              <span>Completed {new Date(project.data.completion_date).toLocaleDateString()}</span>
                            </div>
                          )}
                          {project.data.duration_months && (
                            <div className="flex items-center gap-2">
                              <Target className="w-4 h-4" />
                              <span>{project.data.duration_months} months</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t pt-3 space-y-3">
                          {project.data.scope_description && (
                            <div>
                              <h4 className="font-medium text-sm mb-1">Scope</h4>
                              <p className="text-sm text-gray-700">{project.data.scope_description}</p>
                            </div>
                          )}
                          
                          {project.data.key_deliverables && project.data.key_deliverables.length > 0 && (
                            <div>
                              <h4 className="font-medium text-sm mb-1 flex items-center">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Key Deliverables
                              </h4>
                              <ul className="list-disc pl-4 space-y-1 text-sm">
                                {project.data.key_deliverables.map((deliverable, idx) => (
                                  <li key={idx} className="text-gray-700">{deliverable}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {project.data.outcomes_achieved && project.data.outcomes_achieved.length > 0 && (
                            <div>
                              <h4 className="font-medium text-sm mb-1 flex items-center">
                                <TrendingUp className="w-4 h-4 mr-1" />
                                Outcomes Achieved
                              </h4>
                              <ul className="list-disc pl-4 space-y-1 text-sm">
                                {project.data.outcomes_achieved.map((outcome, idx) => (
                                  <li key={idx} className="text-green-700">{outcome}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {project.data.key_personnel && project.data.key_personnel.length > 0 && (
                            <div>
                              <h4 className="font-medium text-sm mb-1 flex items-center">
                                <Users className="w-4 h-4 mr-1" />
                                Key Personnel
                              </h4>
                              <div className="flex flex-wrap gap-1">
                                {project.data.key_personnel.map((person, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">{person}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {project.data.awards_recognition && project.data.awards_recognition.length > 0 && (
                            <div>
                              <h4 className="font-medium text-sm mb-1 flex items-center">
                                <Award className="w-4 h-4 mr-1" />
                                Awards & Recognition
                              </h4>
                              <ul className="list-disc pl-4 space-y-1 text-sm">
                                {project.data.awards_recognition.map((award, idx) => (
                                  <li key={idx} className="text-purple-700">{award}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            {project.data.photos_available && (
                              <Badge variant="outline" className="flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" />
                                Photos Available
                              </Badge>
                            )}
                            {project.data.references_available && (
                              <Badge variant="outline" className="flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                References Available
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
                        {/* NEW: Phase 5 - Generate Workplan button for ongoing projects */}
                        {(!project.data.completion_date || project.data.project_status === 'ongoing') && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGenerateWorkplan(project);
                            }}
                            className="flex-1"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Generate Workplan
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToProposal(project);
                          }}
                          className="flex-1"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add to Proposal
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            // View project details
                          }}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
              
              {/* Pagination Controls */}
              <Card className="mt-6">
                <div className="flex items-center justify-between p-4">
                  <div className="text-sm text-gray-600">
                    Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount} projects
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 1}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, Math.ceil(totalCount / pageSize)) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <Button
                            key={pageNum}
                            variant={page === pageNum ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPage(pageNum)}
                            className="w-10"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      {Math.ceil(totalCount / pageSize) > 5 && (
                        <>
                          <span className="px-2 text-gray-400">...</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPage(Math.ceil(totalCount / pageSize))}
                            className="w-10"
                          >
                            {Math.ceil(totalCount / pageSize)}
                          </Button>
                        </>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!hasMore}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </>
      )}

      {/* NEW: Phase 5 - Workplan Generation Modal */}
      {showGenerationModal && projectForGeneration && (
        <WorkplanGenerationModal
          project={projectForGeneration}
          selectedApproaches={selectedApproaches}
          selectedCompliance={selectedCompliance}
          onClose={() => {
            setShowGenerationModal(false);
            setProjectForGeneration(null);
          }}
          onGenerate={handleWorkplanGenerated}
        />
      )}
    </div>
  );
}


